import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  Package, 
  ShoppingCart, 
  TrendingUp,
  LogOut,
  Store,
  Bell,
  AlertCircle,
  RefreshCw,
  Globe
} from 'lucide-react';
import { Container } from '@/components/layout/container';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AuthGuard } from '@/features/auth/components/auth-guard';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { getDashboardStats, getOrdersByStatus, Order } from '@/features/orders/api/admin-orders';
import { getDailyCombinedMetrics, type DailyCombinedMetric } from '@/features/analytics/api/daily';

function DashboardContent() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    outForDelivery: 0,
    deliveredToday: 0,
    siteVisits: 0,
    productVisits: 0,
  });
  const [newOrders, setNewOrders] = useState<Order[]>([]);
  const [dailyMetrics, setDailyMetrics] = useState<DailyCombinedMetric[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardStats, submittedOrders, daily] = await Promise.all([
        getDashboardStats(),
        getOrdersByStatus('ORDER_SUBMITTED'),
        getDailyCombinedMetrics({ days: 14 }).catch(() => []),
      ]);
      setStats(dashboardStats);
      setNewOrders(submittedOrders.slice(0, 5));
      setDailyMetrics(daily);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const dashboardStats = [
    { 
      label: 'Total Orders', 
      value: stats.totalOrders.toString(), 
      icon: ShoppingCart, 
      trend: `${stats.pendingOrders} pending`,
      trendTo: '/admin/orders?status=PENDING',
      to: '/admin/orders',
    },
    { 
      label: 'Out for Delivery', 
      value: stats.outForDelivery.toString(), 
      icon: Package, 
      trend: 'Active deliveries',
      to: '/admin/orders?status=ON_THE_WAY',
    },
    { 
      label: 'Delivered Today', 
      value: stats.deliveredToday.toString(), 
      icon: TrendingUp, 
      trend: 'Completed',
      trendTo: '/admin/orders?status=COMPLETED',
      to: '/admin/orders?status=COMPLETED&date=TODAY',
    },
    { 
      label: 'New Orders', 
      value: newOrders.length.toString(), 
      icon: AlertCircle, 
      trend: 'Need confirmation',
      to: '/admin/orders?status=NEW',
    },
    {
      label: 'Site Visits',
      value: stats.siteVisits.toLocaleString(),
      icon: Globe,
      trend: `${stats.productVisits.toLocaleString()} product views`,
      to: '/admin/products',
    },
  ];

  const quickActions = [
    { label: 'Manage Products', icon: Package, onClick: () => navigate('/admin/products') },
    { label: 'View Orders', icon: ShoppingCart, onClick: () => navigate('/admin/orders') },
    { label: 'Manage Branches', icon: Store, onClick: () => navigate('/admin/branches') },
  ];

  const formatKes = (amount: number) => {
    const normalized = Number.isFinite(amount) ? amount : 0;
    return `KES ${normalized.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="min-h-screen bg-brand-50">
      {/* Header */}
      <header className="bg-white border-b border-brand-100">
        <Container>
          <div className="flex min-h-16 flex-col justify-between gap-3 py-4 sm:h-16 sm:flex-row sm:items-center sm:py-0">
            <h1 className="text-xl font-display font-bold text-brand-900">
              Admin Dashboard
            </h1>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchDashboardData}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/orders')}
                className="relative"
              >
                <Bell className="w-5 h-5" />
                {newOrders.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white">
                    {newOrders.length}
                  </Badge>
                )}
              </Button>
              <span className="max-w-full truncate text-sm text-brand-600 sm:text-base">Welcome, {user?.name}</span>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </Container>
      </header>

      <Container className="py-4 sm:py-8">
        {/* Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
          {dashboardStats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.label}
                interactive
                role="button"
                tabIndex={0}
                onClick={() => stat.to && navigate(stat.to)}
                onKeyDown={(e) => {
                  if (!stat.to) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(stat.to);
                  }
                }}
              >
                <CardContent className="p-5 sm:p-6 select-none">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-brand-500">{stat.label}</p>
                      <p className="text-2xl font-bold text-brand-900 mt-1">
                        {stat.value}
                      </p>
                    </div>
                    <div className="p-2 bg-brand-100 rounded-lg">
                      <Icon className="w-5 h-5 text-brand-600" />
                    </div>
                  </div>
                  {stat.trendTo ? (
                    <button
                      type="button"
                      className="mt-2 text-sm text-success-600 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(stat.trendTo);
                      }}
                    >
                      {stat.trend}
                    </button>
                  ) : (
                    <p className="text-sm text-success-600 mt-2">{stat.trend}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.label}
                    variant="outline"
                    className="h-auto min-h-24 flex-col gap-2 py-4 text-center"
                    onClick={action.onClick}
                  >
                    <Icon className="w-6 h-6" />
                    {action.label}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Daily Metrics */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Daily Metrics (Last 14 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <TableContainer>
              <Table striped hover compact>
                <TableHeader>
                  <TableRow variant="header">
                    <TableHead>Day</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Order Total</TableHead>
                    <TableHead className="text-right">Site Visits</TableHead>
                    <TableHead className="text-right">Product Views</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyMetrics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-brand-500">
                        No daily metrics available yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    dailyMetrics
                      .slice()
                      .reverse()
                      .map((row) => (
                        <TableRow key={row.day}>
                          <TableCell className="font-medium text-brand-900">{row.day}</TableCell>
                          <TableCell className="text-right">{row.orderCount.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{formatKes(row.orderTotalAmount)}</TableCell>
                          <TableCell className="text-right">{row.siteVisits.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{row.productViews.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* New Orders Requiring Action */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>New Orders ({newOrders.length})</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/orders')}>
              View All Orders
            </Button>
          </CardHeader>
          <CardContent>
            {newOrders.length === 0 ? (
              <p className="text-brand-500 text-center py-8">
                No new orders requiring confirmation
              </p>
            ) : (
              <div className="space-y-3">
                {newOrders.map((order) => (
                  <div 
                    key={order.id} 
                    className="flex flex-col gap-3 rounded-lg bg-brand-50 p-3 transition-colors hover:bg-brand-100 sm:flex-row sm:items-center sm:justify-between"
                    onClick={() => navigate('/admin/orders?status=NEW')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <ShoppingCart className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-brand-900">
                          Order #{order.id.slice(-6)}
                        </p>
                        <p className="break-words text-sm text-brand-600">
                          {order.customer_name || order.customer_phone} • KES {order.total_amount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <Badge className="bg-blue-100 text-blue-700">
                        New
                      </Badge>
                      <Button size="sm" onClick={(e) => {
                        e.stopPropagation();
                        navigate('/admin/orders?status=NEW');
                      }}>
                        Confirm
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}

export function AdminDashboardPage() {
  return (
    <AuthGuard requiredRole={['admin', 'super-admin']}>
      <DashboardContent />
    </AuthGuard>
  );
}

export default AdminDashboardPage;
